/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Tab, Tabs, withStyles } from '@material-ui/core';
import { Formik } from 'formik';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import classNames from 'classnames';
import queryString from 'query-string';

import {
  getClientContacts,
  getClientContract,
  getClientContractFees,
  getClientContractSLA,
  getClientContractsCreditsAndRebates,
  getClientDetails,
  getClientGeneralNotes,
  getClientIssuesAndChallenges,
  getClientPoliciesImportantNotes,
  getClientSpecialProcessCategoryTypes,
  getClientSpecialProcesses,
  getClientStaticBenefits,
  getHomesaleClosingCosts,
  getHomesaleEligibleProperties,
  getHomesaleGboInspections,
  getHomesaleHHGProcesses,
  setClientFocusedTab,
  setClientId,
  setLoadingComplete,
  getPrebuiltTriggers
} from '../clients.actions';
import {
  getClientHomesaleGeneralInfo,
  getClientLumpSum,
  getClientMetadata,
  getClientPoliciesGeneralInfo,
  getClientPolicyNotes,
  getClientAddressesByClientId
} from '../clients.actions';
import { hideToast, setPageTitle, showToast } from 'modules/layout/layout.actions';
import { getAuthClientPoliciesList } from 'modules/authorizations/store/actions/authorizations.actions';
import { GET_CLIENT_STATIC_BENEFITS_FAILURE, GET_PREBUILT_TRIGGERS_FAILURE } from '../types';

import { CONTRACT, CUSTOM, GENERAL_INFO, PAYROLL, POLICIES, } from '../constants';

import {
  GET_CLIENT_DETAILS_FAILURE
} from '../types';
import { grabFullName } from '../utils';
import ClientContractsTab from './clientContractsTab.component';
import ClientHeader from './clientHeader.component';
import ClientPoliciesTab from './clientPoliciesTab.component';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import GeneralInfoTab from './generalInfoTab.component';
import PayrollContainer from './payroll/payroll.container';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { createTheme, ThemeProvider } from '@mui/material';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import CustomFields from './customFields/customFields.component';
import { GET_VENDORS_FAILURE } from 'modules/authorizations/types';
import { getVendors } from 'modules/authorizations/authorizations.actions';
import { getInternalUsers } from 'modules/common/common.actions';
import {
  getExpenseCodesByClientId,
} from 'modules/authorizations/store/actions/dataManagement.actions';
import { GET_EXPENSE_CODES_BY_CLIENT_ID_FAILURE } from 'modules/authorizations/store/types/dataManagementTypes';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const styles = theme => ({
  box: {
    height: '100%'
  },
  form: {
    backgroundColor: theme.palette.common.white,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(2)
  },
  tabsRoot: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  tab: {
    width: 120,
    minWidth: 120
  }
});

function ClientDetailsView(props) {
  const [reloadData, setReloadData] = useState(true);

  const { location, setPageTitle: propsSetPageTitle, clientsInfo, enumerations } = props;
  const clientInfo = clientsInfo ? clientsInfo[props.match.params.id] : null;

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  //initial load of form
  useEffect(() => {
    propsSetPageTitle('Client Details');

    const { tab } = queryString.parse(location.search);
    if (tab) {
      props.setClientFocusedTab(tab);
    } else {
      props.setClientFocusedTab(GENERAL_INFO);
    }

    setReloadData(true);
  }, [location]);

  //reload data
  useEffect(() => {
    if (reloadData) {
      setReloadData(false);
      loadData();
    }
    async function loadData() {
      props.hideToast();

      const id = +props.match.params.id;

      props.setClientId(id);

      if (!props.staticBenefits) {
        let { type } = await props.getClientStaticBenefits();
        if (type === GET_CLIENT_STATIC_BENEFITS_FAILURE) {
          props.showToast('Failed to retrieve client static benefits, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
      }

      if (!props.vendors) {
        let { type } = await props.getVendors();
        if (type === GET_VENDORS_FAILURE) {
          props.showToast('Failed to retrieve vendors, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
      }

      if (!props.internalUsers) {
        await props.getInternalUsers();
      }

      const prebuiltTriggersResponse = await props.getPrebuiltTriggers();
      if (prebuiltTriggersResponse.type === GET_PREBUILT_TRIGGERS_FAILURE) {
        props.showToast('Failed to retrieve prebuilt triggers, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      await fetchEnumerations(props);

      // Fetch full client info
      if (!clientInfo) {
        const resp = await props.getClientDetails(id);
        if (resp.type === GET_CLIENT_DETAILS_FAILURE) {
          props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          return;
        }
      }

      const latestClientInfo = props.clientsInfo ? props.clientsInfo[id] : null;
      const expenseCodeClientId = latestClientInfo?.clientDetails?.clientDetails?.clientId;

      if (expenseCodeClientId) {
        let { type } = await props.getExpenseCodesByClientId(expenseCodeClientId);
        if (type === GET_EXPENSE_CODES_BY_CLIENT_ID_FAILURE) {
          props.showToast('Failed to retrieve expense codes, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
      }

      props.setLoadingComplete();
    }
  }, [reloadData]);

  const extractHeaderInfo = details => {
    return {
      clientCode: details.clientDetails.clientCode ? details.clientDetails.clientCode : '',
      description: details.clientDetails.shortName ? details.clientDetails.shortName : '',
      acumaticaStatus: details.clientDetails.acumaticaCustomerId ? details.clientDetails.acumaticaCustomerId : 'Does not exist',
      clientStatus:
        enumerations.allEnums && enumerations.allEnums.clientStatusTypes && details.clientDetails.clientStatusId ? (
          enumerations.allEnums.clientStatusTypes.find(cs => cs.id === details.clientDetails.clientStatusId).title
        ) : (
          <p>&ndash;</p>
        ),
      clientContract: details.clientDetails.addedOn ? details.clientDetails.addedOn : '',
      serviceRep: details.employeeNumber ? grabFullName(props.internalUsers, details.employeeNumber) : '',
      id: details.clientDetails.clientRecordId
    };
  };

  const handleTabChange = tab => {
    props.setClientFocusedTab(tab);

    const values = queryString.parse(props.location.search);
    values.tab = tab;
    props.history.replace(`${props.location.pathname}?${queryString.stringify(values)}`);
  };

  const canReadClients = hasPermission(Permissions.Clients.General.Read);
  const canEditClients = hasPermission(Permissions.Clients.General.Edit);

  if (!canReadClients) {
    props.history.push('/');
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box pl={1} pr={1} classes={{ root: classNames(props.classes.box) }}>
        {(props.isLoading || !clientInfo) && <FullscreenSpinner />}
        {!props.isLoading && clientInfo && clientInfo.clientDetails && (
          <div className={props.classes.form}>
            <ClientHeader headerInfo={extractHeaderInfo(clientInfo.clientDetails)} setReloadData={setReloadData} clientRecordId={clientInfo.clientDetails.clientDetails.clientRecordId} hideEdit={!canEditClients} />
            <Tabs
              variant="standard"
              value={props.focusedClientTab}
              onChange={(event, tab) => handleTabChange(tab)}
              classes={{
                root: classNames(props.classes.tabsRoot)
              }}
            >
              <Tab value={GENERAL_INFO} label="General Info" classes={{ root: classNames(props.classes.tab) }} />
              <Tab value={POLICIES} label="Policies" classes={{ root: classNames(props.classes.tab) }} />
              <Tab value={CONTRACT} label="Contract" classes={{ root: classNames(props.classes.tab) }} />
              <Tab value={CUSTOM} label="Custom" classes={{ root: classNames(props.classes.tab) }} />
              <Tab value={PAYROLL} label="Payroll" classes={{ root: classNames(props.classes.tab) }} />
              {/* <Tab value={TASKS} label="Tasks" classes={{ root: classNames(props.classes.tab) }} disabled /> */}
              {/* <Tab value={INTEREST} label="Interest" classes={{ root: classNames(props.classes.tab) }} disabled /> */}
              {/* <Tab value={FEES} label="Fees" classes={{ root: classNames(props.classes.tab) }} disabled /> */}
              {/* <Tab value={OPTIONS} label="Options" classes={{ root: classNames(props.classes.tab) }} disabled /> */}
              {/* <Tab value={USERS} label="Users" classes={{ root: classNames(props.classes.tab) }} disabled /> */}
            </Tabs>

            <Formik
              initialValues={clientInfo.clientDetails}
              // validate={validate}
              // onSubmit={handleSaveRequest}
              enableReinitialize
              validateOnBlur={true}
              validateOnChange={true}
            >
              {formikProps => (
                <>
                  {props.focusedClientTab === GENERAL_INFO && (
                    <GeneralInfoTab
                      formikProps={formikProps}
                      clientDetails={clientInfo.clientDetails}
                      enumerations={props.enumerations}
                      vendors={props.vendors}
                      internalUsers={props.internalUsers}
                      isReadOnly={!canEditClients}
                      history={props.history}
                    />
                  )}
                  {props.focusedClientTab === PAYROLL && <PayrollContainer formikProps={formikProps} isReadOnly={!canEditClients} />}
                  {props.focusedClientTab === CONTRACT && (
                    <ClientContractsTab
                      formikProps={formikProps}
                      clientDetails={clientInfo.clientDetails}
                      enumerations={props.enumerations}
                      isReadOnly={!canEditClients}
                    />
                  )}
                  {props.focusedClientTab === POLICIES && (
                    <ClientPoliciesTab
                      clientDetails={clientInfo.clientDetails}
                      enumerations={props.enumerations}
                      formikProps={formikProps}
                      prebuiltTriggers={props.prebuiltTriggers}
                      expenseCodes={props.expenseCodes}
                      isReadOnly={!canEditClients}
                    />
                  )}
                  {props.focusedClientTab === CUSTOM && (
                    <CustomFields clientDetails={clientInfo.clientDetails} enumerations={props.enumerations} formikProps={formikProps} isReadOnly={!canEditClients} />
                  )}
                </>
              )}
            </Formik>
          </div>
        )}
      </Box>
    </ThemeProvider>
  );
}

ClientDetailsView.propTypes = {
  classes: PropTypes.object.isRequired,
  clientDetails: PropTypes.object.isRequired,
  internalUsers: PropTypes.array,
  setClientFocusedTab: PropTypes.func.isRequired,
  setPageTitle: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  getClientDetails: PropTypes.func.isRequired,
  getClientContacts: PropTypes.func.isRequired,
  getClientIssuesAndChallenges: PropTypes.func.isRequired,
  getClientGeneralNotes: PropTypes.func.isRequired,
  getClientContractSLA: PropTypes.func.isRequired,
  getClientContractFees: PropTypes.func.isRequired,
  getClientContractsCreditsAndRebates: PropTypes.func.isRequired,
  getClientContract: PropTypes.func.isRequired,
  getClientPoliciesImportantNotes: PropTypes.func.isRequired,
  getClientLumpSum: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  isLoading: PropTypes.bool.isRequired,
  showToast: PropTypes.func.isRequired,
  hideToast: PropTypes.func.isRequired,
  focusedClientTab: PropTypes.string,
  theme: PropTypes.object,
  getClientSpecialProcesses: PropTypes.func.isRequired,
  getClientSpecialProcessCategoryTypes: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { focusedClientTab, isLoading, metadata, staticBenefits, prebuiltTriggers } = state.clients;
  const { clientPolicies } = state.authorizations;
  const clientsInfo = state.clients.clientsInfo;
  const enumerations = state.enumerations;
  const { vendors } = state.authorizations;
  const { internalUsers } = state.common;
  const { expenseCodes } = state.authorizations || {};

  return {
    focusedClientTab,
    isLoading,
    clientsInfo,
    internalUsers,
    metadata,
    clientPolicies,
    enumerations,
    staticBenefits,
    vendors,
    prebuiltTriggers,
    expenseCodes
  };
};

export default compose(
  withStyles(styles),
  withRouter,
  withTheme,
  connect(mapStateToProps, {
    setClientFocusedTab,
    getClientDetails,
    getInternalUsers,
    getClientContacts,
    getClientIssuesAndChallenges,
    getClientGeneralNotes,
    getClientContractSLA,
    getClientContractFees,
    getClientContractsCreditsAndRebates,
    getClientContract,
    getHomesaleClosingCosts,
    getHomesaleEligibleProperties,
    getHomesaleGboInspections,
    getHomesaleHHGProcesses,
    setPageTitle,
    hideToast,
    showToast,
    getClientHomesaleGeneralInfo,
    getClientLumpSum,
    getClientPolicyNotes,
    getClientMetadata,
    getClientSpecialProcesses,
    getClientSpecialProcessCategoryTypes,
    getClientPoliciesImportantNotes,
    getClientPoliciesGeneralInfo,
    setClientId,
    setLoadingComplete,
    getAuthClientPoliciesList,
    getClientAddressesByClientId,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getClientStaticBenefits,
    getVendors,
    getPrebuiltTriggers,
    getExpenseCodesByClientId
  })
)(ClientDetailsView);